import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { fitCameraToDesign } from '../utils/fit-camera.js';

// Authored against the same fixed 1024x576 design space as the other full-screen overlays.
const DESIGN_WIDTH = 1024;
const DESIGN_HEIGHT = 576;

// Premium emerald / gold palette for the celebration.
const COLOR_EMERALD = 0x10b981;
const COLOR_GOLD = 0xfacc15;

/**
 * @typedef EvolutionSceneData
 * @type {object}
 * @property {import('../utils/evolution-utils.js').EvolutionResult[]} evolutions one or more evolutions to celebrate, shown in sequence
 */

/**
 * Celebratory overlay shown OUTSIDE of battle when one or more party monsters evolve. It renders a
 * dark scrim with the old sprite, a quick flash/scale "morph", then the new sprite + a
 * "<Old> evolved into <New>!" banner. Tap / click / SPACE advances to the next evolution (if the
 * party produced several) and finally dismisses the scene. Purely cosmetic — it pauses nothing.
 */
export class EvolutionScene extends BaseScene {
  /** @type {import('../utils/evolution-utils.js').EvolutionResult[]} */
  #evolutions;
  /** @type {number} */
  #index;
  /** @type {boolean} */
  #isAnimating;
  /** @type {Phaser.GameObjects.Container} */
  #stage;
  /** @type {Phaser.GameObjects.Text} */
  #titleText;
  /** @type {Phaser.GameObjects.Text} */
  #hintText;

  constructor() {
    super({ key: SCENE_KEYS.EVOLUTION_SCENE });
  }

  /**
   * @param {EvolutionSceneData} data
   * @returns {void}
   */
  init(data) {
    super.init(data);
    this.#evolutions = Array.isArray(data?.evolutions) ? data.evolutions : [];
    this.#index = 0;
    this.#isAnimating = false;
  }

  /**
   * @returns {void}
   */
  create() {
    super.create();
    // Sole input target: mute the world/HUD input + sleep the HUD while the celebration is up, so a
    // stray tap can't reach the world and the player can't act underneath (restored on shutdown).
    this._claimOverlayInput();

    // Nothing to show: close immediately rather than leave an empty overlay up.
    if (this.#evolutions.length === 0) {
      this.scene.stop();
      return;
    }

    fitCameraToDesign(this, DESIGN_WIDTH, DESIGN_HEIGHT);

    // dark scrim across the whole design space
    this.add.rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT, 0x040a08, 0.92).setOrigin(0);

    // a soft emerald glow framing the centre stage
    const glow = this.add.circle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 30, 220, COLOR_EMERALD, 0.12);
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.08, to: 0.2 },
      scale: { from: 0.95, to: 1.05 },
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: Phaser.Math.Easing.Sine.InOut,
    });

    // container that holds the morphing sprite (centred)
    this.#stage = this.add.container(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 30, []);

    this.#titleText = this.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT - 110, '', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        color: '#fde68a',
        fontSize: '34px',
        align: 'center',
        wordWrap: { width: DESIGN_WIDTH - 120 },
      })
      .setOrigin(0.5);

    this.#hintText = this.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT - 48, 'tap / SPACE to continue', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        color: '#6ee7b7',
        fontSize: '20px',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    // dismiss / advance on tap or click anywhere
    this.input.on(Phaser.Input.Events.POINTER_DOWN, () => this.#advance());

    this.#playEvolution();
  }

  /**
   * @returns {void}
   */
  update() {
    super.update();
    if (this._controls?.wasSpaceKeyPressed()) {
      this.#advance();
    }
  }

  /**
   * Animate the current evolution: show the old sprite, flash + scale "morph", then reveal the new
   * sprite with its banner. Sets #isAnimating so taps during the morph are ignored.
   * @returns {void}
   */
  #playEvolution() {
    const evo = this.#evolutions[this.#index];
    if (!evo) {
      this.scene.stop();
      return;
    }

    this.#isAnimating = true;
    this.#stage.removeAll(true);
    this.#titleText.setText('');
    this.#hintText.setAlpha(0);

    const oldSprite = this.add
      .image(0, 0, evo.fromAssetKey, evo.fromAssetFrame)
      .setScale((evo.fromAssetScale || 1) * 1.4);
    this.#stage.add(oldSprite);

    // morph: shrink + flash white, swap to the new sprite, then pop it in
    this.tweens.add({
      targets: oldSprite,
      scale: { from: (evo.fromAssetScale || 1) * 1.4, to: (evo.fromAssetScale || 1) * 0.2 },
      alpha: { from: 1, to: 0 },
      duration: 650,
      ease: Phaser.Math.Easing.Quadratic.In,
      onComplete: () => {
        oldSprite.destroy();
        this.#flash();

        const newSprite = this.add
          .image(0, 0, evo.toAssetKey, evo.toAssetFrame)
          .setScale((evo.toAssetScale || 1) * 0.2)
          .setAlpha(0);
        this.#stage.add(newSprite);

        this.tweens.add({
          targets: newSprite,
          scale: { from: (evo.toAssetScale || 1) * 0.2, to: (evo.toAssetScale || 1) * 1.4 },
          alpha: { from: 0, to: 1 },
          duration: 700,
          ease: Phaser.Math.Easing.Back.Out,
          onComplete: () => {
            this.#titleText.setText(`${evo.fromName} evolved into ${evo.toName}!`);
            this.#hintText.setAlpha(1);
            this.#isAnimating = false;
          },
        });
      },
    });
  }

  /**
   * Brief full-screen gold flash to sell the "morph" moment.
   * @returns {void}
   */
  #flash() {
    const flash = this.add.rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT, COLOR_GOLD, 0.0).setOrigin(0);
    this.tweens.add({
      targets: flash,
      alpha: { from: 0.55, to: 0 },
      duration: 450,
      ease: Phaser.Math.Easing.Quadratic.Out,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Advance to the next queued evolution, or close the overlay when the last one has been shown.
   * Ignored while a morph animation is mid-flight so players can't skip the reveal accidentally.
   * @returns {void}
   */
  #advance() {
    if (this.#isAnimating) {
      return;
    }
    this.#index += 1;
    if (this.#index >= this.#evolutions.length) {
      this.scene.stop();
      return;
    }
    this.#playEvolution();
  }
}
